<template>
  <div>
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi mdi-account-clock"></span></div>
      <div>
        <div class="id-h1">{{ $t('approve.title') }}</div>
        <div class="pg-sub">{{ $t('approve.subtitle') }}</div>
      </div>
    </div>

    <div class="scard" style="padding:0">
      <div v-if="newStudentList.length === 0" style="padding:24px 18px;color:var(--c-muted);text-align:center">
        {{ $t('approve.noData') }}
      </div>
      <div v-for="(p, i) in newStudentList" :key="i"
        style="padding:16px 18px;border-bottom:1px solid var(--c-border)">
        <div class="row" style="justify-content:space-between">
          <div class="row" style="gap:12px">
            <span class="tt-avatar" style="width:44px;height:44px;background:var(--c-primary)">{{ (p.fullname || '?').charAt(0) }}</span>
            <div class="col" style="gap:1px">
              <span class="strong" style="font-size:16px">{{ p.fullname }}</span>
              <!-- ช่องว่างไม่ต่อด้วยจุดลอย ๆ · username มีป้ายกำกับ ไม่งั้นแอดมินไม่รู้ว่าเป็นของใคร -->
              <span class="t-cap">{{ [p.gender, calculateAge(p.dateofbirth).text, p.school,
                p.username ? $t('dup.acctCustomer', { u: p.username }) : '', p.mobileno].filter(Boolean).join(' · ') }}</span>
            </div>
          </div>
          <div class="row" style="gap:8px">
            <!-- ⚠️ ใบที่ล็อกไว้ใช้ aria-disabled + guard ในตัวจัดการ ไม่ใช่ disabled จริง —
                 เบราว์เซอร์ตัด event ของปุ่มที่ disabled ทำให้ v-hint ไม่ทำงาน คนใช้เลยไม่รู้ว่าทำไมกดไม่ได้
                 (กติกาใน CLAUDE.md หัวข้อ Hint/tooltip) · ปุ่ม "ตีกลับ" ไม่ถูกล็อก ตั้งใจ -->
            <button class="id-btn id-btn-success id-btn-sm"
              :aria-disabled="headOnlyBlocked(p) ? 'true' : null"
              v-hint="headOnlyBlocked(p) ? $t('dup.headOnlyHint') : ''"
              @click="startApprove(p)"><span class="mdi mdi-check"></span> {{ $t('approve.approveBtn') }}</button>
            <button class="id-btn id-btn-ghost id-btn-sm" @click="clickDeleteNewStudent(p)">{{ $t('approve.rejectBtn') }}</button>
          </div>
        </div>
        <!-- เตือนว่าเด็กคนนี้อาจมีแถวอยู่แล้ว (ยุคที่แอดมินสร้างให้ใต้บัญชีพนักงาน)
             อนุมัติไปเลย = เกิดข้อมูลสองชุดทันที · backend แนบมากับ /getNewStudentList -->
        <div v-if="(p.possibleDuplicates || []).length" class="apv-dup">
          <div class="apv-dup-head">
            <span class="mdi mdi-account-alert-outline"></span>
            <b>{{ $t('dup.approveWarn', { n: p.possibleDuplicates.length }) }}</b>
          </div>
          <!-- บอกตรง ๆ ว่าใบนี้ถูกล็อก ไม่ใช่ปล่อยให้กดแล้วเด้ง error ทีหลัง -->
          <div v-if="headOnlyBlocked(p)" class="apv-dup-lock">
            <span class="mdi mdi-lock-outline"></span> {{ $t('dup.headOnlyHint') }}
          </div>
          <!-- ทีละคู่: ตารางเทียบสองฝั่ง ช่องที่ตรงกันเน้นสี = เห็นทันทีว่าทำไมระบบคิดว่าซ้ำ -->
          <div v-for="d in p.possibleDuplicates" :key="d.studentid" class="apv-cmp">
            <div class="apv-cmp-why">
              <span class="dup-conf" :class="'cf-' + d.confidence">{{ $t('dup.conf.' + d.confidence) }}</span>
              <span>{{ $t('dup.whyLabel') }}: <b>{{ $t('dup.why.' + d.matchKey) }}</b></span>
            </div>
            <div class="apv-cmp-grid">
              <div></div>
              <div class="apv-cmp-col">{{ $t('dup.cmpExisting') }}</div>
              <div class="apv-cmp-col">{{ $t('dup.cmpPending') }}</div>
              <template v-for="r in cmpRows(p, d)" :key="r.key">
                <div class="apv-cmp-label">{{ r.label }}</div>
                <div :class="{ same: r.same }">{{ r.existing }}<span v-if="r.existingTag" class="apv-tag">{{ r.existingTag }}</span></div>
                <div :class="{ same: r.same }">{{ r.pending }}<span v-if="r.pendingTag" class="apv-tag">{{ r.pendingTag }}</span></div>
              </template>
            </div>
            <div v-if="linkable(d)" class="apv-cmp-foot">
              <span class="apv-dup-hint">{{ $t('dup.linkHint') }}</span>
              <button class="id-btn id-btn-primary id-btn-sm" @click="startLink(p, d)">
                <span class="mdi mdi-link-variant"></span> {{ $t('dup.linkBtn') }}</button>
            </div>
          </div>
          <div class="apv-dup-hint" style="margin:8px 0 0">{{ $t('dup.approveWarnHint') }}</div>
        </div>
      </div>
    </div>

    <!-- v-dialog ไม่ต้องมี activator slot -->
    <id-modal v-model="dialogConfirmApprove" size="sm" icon="mdi-account-check-outline" :title="$t('btn.confirm')" persistent>
      <p style="margin:0">{{ $t('approve.confirmApprove', { count: confirmStudentList.length }) }}</p>
      <!-- ใบที่ระบบเตือนว่าซ้ำต้องบอกเหตุผลก่อน — เหตุผลถูกเก็บลง audit ให้ผู้จัดการอ่านย้อนหลัง
           backend บังคับซ้ำอีกชั้น ปุ่มที่ disable ไว้ตรงนี้เป็นแค่ด่านแรกให้ผู้ใช้รู้ตัว -->
      <div v-if="approveNeedsReason" style="margin-top:14px">
        <div class="modal-sec"><span class="mdi mdi-comment-alert-outline"></span> {{ $t('dup.reasonTitle') }}</div>
        <div class="apv-dup-hint" style="margin-top:0">{{ $t('dup.reasonHint') }}</div>
        <textarea class="id-input id-textarea" rows="2" maxlength="500"
          v-model="approveReason" :placeholder="$t('dup.reasonPlaceholder')"></textarea>
      </div>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="clickCancelApprove">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" :disabled="approveNeedsReason && !approveReason.trim()"
          @click="clickConfirmApprove"><span class="mdi mdi-check"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>
    <id-modal v-model="dialogStudentNewDelete" size="sm" icon="mdi-delete-alert-outline" :title="$t('common.confirmDeleteTitle')" persistent>
      <p style="margin:0">{{ $t('approve.confirmDelete') }}</p>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="clickCancelDeleteStd">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" style="background:var(--c-error)" @click="clickConfirmDeleteStd"><span class="mdi mdi-delete"></span> {{ $t('btn.ok') }}</button>
      </template>
    </id-modal>
    <id-modal v-model="dialogLink" size="md" icon="mdi-link-variant" :title="$t('dup.linkTitle')" persistent>
      <template v-if="linkObj">
        <table class="lk-table">
          <thead><tr><th>{{ $t('dup.colField') }}</th><th>{{ $t('dup.colNow') }}</th><th>{{ $t('dup.colAfter') }}</th></tr></thead>
          <tbody>
            <tr v-for="r in linkRows" :key="r.key" :class="{ changed: r.changed }">
              <td>{{ r.label }}</td>
              <td>{{ r.now }}<span v-if="r.nowTag" class="apv-tag">{{ r.nowTag }}</span></td>
              <td>{{ r.after }}<span v-if="r.afterTag" class="apv-tag">{{ r.afterTag }}</span><span v-if="r.changed" class="lk-chg">{{ $t('dup.changed') }}</span></td>
            </tr>
          </tbody>
        </table>
        <div class="lk-what">{{ $t('dup.linkIntro') }}</div>
        <ol class="lk-steps">
          <li>{{ $t('dup.linkStep1', { to: linkObj.pending.username || '-' }) }}</li>
          <li>{{ $t('dup.linkStep2') }}</li>
          <li>{{ $t('dup.linkStep3', { n: linkObj.target.bookings }) }}</li>
          <li>{{ $t('dup.linkStep4') }}</li>
        </ol>
      </template>
      <template #footer>
        <button class="id-btn id-btn-ghost" @click="dialogLink = false">{{ $t('btn.cancel') }}</button>
        <button class="id-btn id-btn-primary" @click="confirmLink"><span class="mdi mdi-link-variant"></span> {{ $t('dup.linkBtn') }}</button>
      </template>
    </id-modal>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      loadingNewStudentList: false,
      confirmStudentList: [],
      newStudentList: [],
      dialogConfirmApprove: false,
      dialogStudentNewDelete: false,
      studentNewDeleteObj: null,
      approveReason: '',
      dialogLink: false,
      linkObj: null,   // { pending, target } ของปุ่ม "ผูกกับเด็กคนนี้"
      newStudentlistHeadersData: []
    }
  },
  methods: {
    // ระดับความมั่นใจของใบ = ตัวที่สูงสุด (backend เรียง veryhigh มาก่อนแล้ว)
    dupConfidence(p) {
      const list = p.possibleDuplicates || [];
      return list.length ? list[0].confidence : '';
    },
    // veryhigh (ชื่อ+นามสกุลตรงกัน หรือ ชื่อเล่น+นามสกุล+วันเกิด) = หัวเท่านั้นที่อนุมัติได้ · backend บังคับซ้ำอีกชั้น
    // ⚠️ ถ้าอ่าน usertype ไม่ได้ (userdata ว่าง) ให้ **ไม่ล็อก** แล้วปล่อยให้เซิร์ฟเวอร์ตัดสิน —
    // ด่านฝั่งจอมีไว้บอกผู้ใช้ล่วงหน้าเท่านั้น ห้ามเป็นตัวที่ปฏิเสธคนผิด · เส้นทาง ForgotPassword
    // dispatch setToken ด้วย token อย่างเดียว (ไม่มี userdata) ถ้าล็อกไว้ ผู้จัดการที่เพิ่งรีเซ็ต
    // รหัสจะกดอนุมัติใบของตัวเองไม่ได้จนกว่าจะรีโหลด ทั้งที่ backend อนุญาต
    headOnlyBlocked(p) {
      if (!this.knownUsertype) return false;
      // veryhigh + high (เจ้าของเคาะ 2026-10-16) — ตรงกับ DUP_HEAD_ONLY_CONFIDENCE ฝั่ง backend
      return ['veryhigh', 'high'].includes(this.dupConfidence(p)) && !this.isHead;
    },
    // ผูกได้เฉพาะคู่ที่มั่นใจ (high/veryhigh) และแถวเดิมอยู่ใต้บัญชีพนักงานหรือไม่มีครอบครัว —
    // แถวของครอบครัวลูกค้าอื่นห้ามดึงข้ามบ้าน · backend เช็คซ้ำทุกข้อ ตรงนี้แค่ซ่อนปุ่มที่กดแล้วจะโดนปฏิเสธ
    // ปุ่มนี้ = หัว/แอดมิน (backend ใช้ isHeadOrAdmin) · โค้ชที่ได้เมนูนี้ไม่เห็นปุ่มที่กดแล้วโดน 403 แน่ ๆ
    // อ่าน usertype ไม่ได้ = ไม่ซ่อน ให้ backend ตัดสิน (ด่านฝั่งจอห้ามปฏิเสธคนผิด)
    linkable(d) {
      if (this.knownUsertype && !['0', '1'].includes(String(this.userdata.usertype))) return false;
      return ['veryhigh', 'high'].includes(d.confidence) && (d.staffFamily || d.familyid == null);
    },
    // ป้ายบัญชีของเด็กที่มีอยู่แล้ว — "tnpl1" เฉย ๆ แอดมินไม่รู้ว่าคือบัญชีพนักงานที่ฝากเด็กไว้
    // หัวแถว "อยู่ในบัญชี" บอกแล้วว่าเป็น username — ช่องจึงเป็นชื่อบัญชีล้วน ส่วนบัญชีพนักงานมีป้ายต่อท้าย
    acctName(d) { return d.family_username || this.$t('dup.acctNone'); },
    // ป้ายประเภทบัญชีสั้น ๆ ("บัญชีแอดมิน" / "บัญชีลูกค้า") ตาม usertype ของเจ้าของบัญชี — ไม่รู้ประเภท = ไม่มีป้าย
    acctTag(usertype) {
      const k = String(usertype ?? '');
      return ['0', '1', '2', '10'].includes(k) ? this.$t('dup.acctType.' + k) : '';
    },
    fmtDob(v) { return v ? this.$formatShortDate(String(v).slice(0, 10)) : '—'; },
    // เทียบแบบเดียวกับ backend (ตัดช่องว่าง/ตัวพิมพ์) เพื่อเน้นช่องที่ตรงกัน — แค่ไฮไลต์ ไม่ใช่ตัวตัดสิน
    sameText(a, b) {
      const n = (v) => String(v || '').replace(/s+/g, ' ').trim().toLowerCase();
      return !!n(a) && n(a) === n(b);
    },
    cmpRows(p, d) {
      const pName = [p.firstname, p.middlename, p.lastname].filter(Boolean).join(' ');
      return [
        { key: 'name', label: this.$t('dup.fName'), existing: d.fullname, pending: pName, same: this.sameText(pName, d.fullname) },
        { key: 'nick', label: this.$t('dup.fNick'), existing: d.nickname || '—', pending: p.nickname || '—', same: this.sameText(p.nickname, d.nickname) },
        { key: 'dob', label: this.$t('dup.fDob'), existing: this.fmtDob(d.dateofbirth), pending: this.fmtDob(p.dob_ymd), same: !!p.dob_ymd && p.dob_ymd === d.dateofbirth },
        { key: 'acct', label: this.$t('dup.fAccount'), existing: this.acctName(d), existingTag: d.family_username ? this.acctTag(d.family_usertype) : '', pending: p.username || '—', pendingTag: p.username ? this.acctTag(p.family_usertype) : '', same: false },
        { key: 'hist', label: this.$t('dup.fHistory'), existing: this.$t('dup.historyN', { n: d.bookings }), pending: this.$t('dup.newRequest'), same: false },
      ];
    },
    startLink(p, d) {
      this.linkObj = { pending: p, target: d };
      this.dialogLink = true;
    },
    async confirmLink() {
      const { pending, target } = this.linkObj;
      this.dialogLink = false;
      this.$emit('onLoading', true);
      try {
        const res = await axios.post(this.baseURL + '/linkPendingStudent',
          { studentid: pending.studentid, targetStudentId: target.studentid },
          { headers: { Authorization: `Bearer ${this.$store.getters.getToken}` } });
        if (res.data.success) {
          this.$emit('onUpdateDataSuccess');
          this.$emit('onInfoHandler', this.$t('dup.linkDone'));
        } else {
          // แปลจาก code (5 ภาษา) · code ที่จอไม่รู้จักค่อยใช้ข้อความไทยของ backend
          const codes = ['gone', 'not_candidate', 'shared_course', 'already_in_family', 'bad_family'];
          this.$emit('onErrorHandler', codes.includes(res.data.code) ? this.$t('dup.linkErr.' + res.data.code)
            : (res.data.message || this.$t('dup.linkFail')));
        }
      } catch (error) {
        this.$emit('onErrorHandler', (error.response && error.response.data && error.response.data.message) || this.$t('dup.linkFail'));
      }
      this.linkObj = null;
      await this.getNewStudentList();
      this.$emit('onLoading', false);
    },
    startApprove(p) {
      if (this.headOnlyBlocked(p)) {
        // ⚠️ ต้องเป็น onErrorHandler ไม่ใช่ onInfoHandler — App.vue ผูก onInfoHandler ไว้กับกล่อง
        // "Success" สีเขียว (onShowInfoDialog → successDialog) ข้อความปฏิเสธในกล่องเขียวอ่านแล้วขัด
        this.$emit('onErrorHandler', this.$t('dup.headOnlyHint'));
        return;
      }
      this.confirmStudentList = [p];
      this.approveReason = '';
      this.openConfirmApprove();
    },
    async getNewStudentList() {
      this.loadingNewStudentList = true
      const token = this.$store.getters.getToken;
      await axios.get(this.baseURL + '/getNewStudentList',
        {
          headers: { Authorization: `Bearer ${token}`, }
        })
        .then(response => {
          //console.log('getNewStudentList res : ', response)
          if (response.data.results.length == 0) {
            this.newStudentList = []
            this.loadingNewStudentList = false
          } else {
            //console.log('getNewStudentList res : ', response.data.results)
            this.newStudentList = response.data.results
            this.loadingNewStudentList = false
          }
        })
        .catch(() => {
          //console.log(error)
        })
    },

    async approveNewStudent() {
      this.$emit('onLoading', true)
      //console.log('approveNewStudent : ', this.newStudentList)
      //console.log('confirmStudentList : ', this.confirmStudentList)
      // แนบเหตุผลไปกับใบที่มีคำเตือน — backend เก็บลง taction_log.reason และปฏิเสธถ้าไม่มี
      const reason = this.approveReason.trim();
      const apprObj = this.confirmStudentList.map((p) => (
        (p.possibleDuplicates || []).length ? { ...p, reason } : p
      ));
      const token = this.$store.getters.getToken;
      await axios.post(this.baseURL + '/approveNewStudent', {
        apprObj: apprObj
      },
        {
          headers: { Authorization: `Bearer ${token}`, }
        })
        .then(response => {
          // เดิมไม่เช็ค success เลย (ยิงแล้วรายงานสำเร็จทันที) — เช็คก่อน แล้วข้อความสำเร็จใช้ $t()
          // เท่านั้น เพราะ /approveNewStudent ตอบอังกฤษตายตัว (บั๊กเดียวกับ fe #192)
          this.$emit('onUpdateDataSuccess');
          if (response.data.success) {
            this.$emit('onInfoHandler', this.$t('msg.studentApproved'));
          } else {
            this.$emit('onErrorHandler', response.data.message || this.$t('msg.approveFail'));
          }
        })
        .catch(error => {

          this.$emit('onErrorHandler', error.message || this.$t('msg.approveFail'));
          //console.log(error)
        })

      this.dialogConfirmApprove = false;
      this.confirmStudentList = []
      this.approveReason = ''
      this.$emit('onLoading', false)
    },
    async deleteNewStudent() {
      this.$emit('onLoading', true)
      //console.log('deleteNewStudent : ', this.studentNewDeleteObj)
      const token = this.$store.getters.getToken;
      await axios.post(this.baseURL + '/deleteStudent', {
        familyid: this.studentNewDeleteObj.familyid,
        studentid: this.studentNewDeleteObj.studentid,
        journal: "1"
      },
        {
          headers: { Authorization: `Bearer ${token}`, }
        })
        .then(response => {
          this.dialogStudentNewDelete = false;

          // เดิมไม่เช็ค success เช่นกัน — เหตุผลเดียวกับ approve ด้านบน
          this.$emit('onUpdateDataSuccess');
          if (response.data.success) {
            this.$emit('onInfoHandler', this.$t('msg.studentDeleted'));
          } else {
            this.$emit('onErrorHandler', response.data.message || this.$t('msg.studentDeleteFail'));
          }
        })
        .catch(error => {
          this.dialogStudentNewDelete = false;
          this.$emit('onErrorHandler', error.message || this.$t('msg.studentDeleteFail'));
          //console.log(error)
        })
      this.studentNewDeleteObj = null
      this.$emit('onLoading', false)
    },
    async clickConfirmDeleteStd() {
      this.dialogStudentNewDelete = false
      await this.deleteNewStudent()
      await this.getNewStudentList()
    },
    clickCancelDeleteStd() {
      this.studentNewDeleteObj = null
      this.dialogStudentNewDelete = false
    },
    async clickConfirmApprove() {
      this.dialogConfirmApprove = false
      await this.approveNewStudent()
      await this.getNewStudentList()
    },
    clickCancelApprove() {
      this.dialogConfirmApprove = false
      this.approveReason = ''
    },
    clickDeleteNewStudent(item) {
      this.dialogStudentNewDelete = true
      this.studentNewDeleteObj = item
    },
    convertToSQL(arrObj) {
      arrObj.forEach(obj => {
        obj.dateofbirth = this.SQLDate(obj.dateofbirth);
      });
      return arrObj;
    },
    SQLDate(date) {
      return moment(date).format('YYYY-MM-DD')
    },
    format_date(value) {
      if (value) {
        return moment(value).format('DD/MM/YYYY')
      }
    },
    calculateAge(birthDate) {
      if (!birthDate) return { text: "", int: 0 };

      const currentDate = new Date();
      if (new Date(birthDate) > currentDate) {
          this.birthDate = null;
          this.years = null;
          this.months = null;
          this.days = null;
          alert(this.$t("common.invalidDob"));
      }

      const diffTime = currentDate - new Date(birthDate);
      const totalDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      let years = Math.floor(totalDays / 365.25);
      let months = Math.floor((totalDays % 365.25) / 30.4375);
      return {
          text : years + " " + this.$t('table.yearAbbr') + " " + months + " " + this.$t('table.monthAbbr'),
          int : years+'.'+months
      }
    },
    openConfirmApprove() {
      if (this.confirmStudentList.length === 0) {
        this.$emit('onErrorHandler', this.$t('approve.selectAtLeastOne'));
        return;
      }
      this.dialogConfirmApprove = true;
    },
  },
  async created() {
    this.$emit('onLoading', true)
    await this.getNewStudentList()
    this.$emit('onLoading', false)
  },
  computed: {
    // ตารางก่อน/หลังใน dialog ผูก — ตามกติกา backend: ช่องที่ผู้ปกครองกรอกทับของเดิม ช่องว่างคงค่าเดิม
    linkRows() {
      if (!this.linkObj) return [];
      const { pending: p, target: d } = this.linkObj;
      const pick = (nv, ov) => (String(nv || '').trim() ? nv : ov);
      const pName = [p.firstname, p.middlename, p.lastname].filter(Boolean).join(' ');
      const rows = [
        { key: 'name', label: this.$t('dup.fName'), now: d.fullname, after: pick(pName, d.fullname) },
        { key: 'nick', label: this.$t('dup.fNick'), now: d.nickname || '—', after: pick(p.nickname, d.nickname) || '—' },
        { key: 'dob', label: this.$t('dup.fDob'), now: this.fmtDob(d.dateofbirth), after: this.fmtDob(p.dob_ymd || d.dateofbirth) },
        { key: 'acct', label: this.$t('dup.fAccount'), now: this.acctName(d), nowTag: d.family_username ? this.acctTag(d.family_usertype) : '', after: p.username || '-', afterTag: this.acctTag(p.family_usertype) },
      ];
      return rows.map((r) => ({ ...r, changed: !this.sameText(r.now, r.after) }));
    },
    ...mapGetters({
      token: 'getToken',
      userdata: 'getUserdata',
    }),
    // usertype 0 = ผู้จัดการ (head) · เป็นด่านฝั่งจอเท่านั้น backend เช็คของจริงอีกชั้น
    knownUsertype() {
      const t = (this.userdata || {}).usertype;
      return t !== undefined && t !== null && t !== '';
    },
    isHead() {
      return String((this.userdata || {}).usertype) === '0';
    },
    // ใบไหนมีคำเตือนซ้ำ = ต้องกรอกเหตุผลก่อนอนุมัติ (ทุกระดับ ไม่ใช่เฉพาะ veryhigh)
    approveNeedsReason() {
      return this.confirmStudentList.some((p) => (p.possibleDuplicates || []).length > 0);
    },
    newStudentlistHeaders() {
      return [
        { title: this.$t('table.name'), key: 'fullname' },
        { title: this.$t('table.gender'), key: 'gender', align: 'left' },
        { title: this.$t('table.age'), key: 'dateofbirth', align: 'left' },
        { title: this.$t('table.school'), key: 'school', align: 'left' },
        { title: this.$t('table.parentUsername'), key: 'username', align: 'center' },
        { title: this.$t('table.phone'), key: 'mobileno', align: 'center' },
        { title: this.$t('table.delete'), key: 'delete', align: 'center', sortable: false },
      ]
    },
  },
}
</script>

<style scoped>
/* เตือน "อาจซ้ำกับเด็กที่มีอยู่แล้ว" บนใบรออนุมัติ — ชื่อคลาส .apv-dup-* ยังไม่มีที่อื่นใช้ */
.apv-dup {
  margin: 10px 0 0 56px; padding: 10px 12px; border-radius: 10px;
  background: color-mix(in srgb, var(--c-warning) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--c-warning) 45%, transparent);
}
.apv-dup-head { display: flex; align-items: center; gap: 6px; color: var(--c-text-heading); font-size: 13.5px; }
.apv-dup-head .mdi { color: var(--c-warning); font-size: 18px; }
.apv-dup-hint { margin: 3px 0 7px; font-size: 12.5px; color: var(--c-text-muted); line-height: 1.5; }
.apv-dup-row { display: flex; flex-wrap: wrap; gap: 2px 10px; padding: 4px 0; border-top: 1px dashed var(--c-border); }
/* แถบ "ล็อกไว้ให้ผู้จัดการอนุมัติ" — เข้มกว่ากล่องเตือนรอบนอกเพราะมันคือข้อจำกัดจริง ไม่ใช่คำแนะนำ */
.apv-dup-lock {
  display: flex; align-items: center; gap: 6px; margin: 0 0 7px;
  padding: 6px 9px; border-radius: 8px; font-size: 12.5px; font-weight: 600;
  color: var(--c-text-heading);
  background: color-mix(in srgb, var(--c-error) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--c-error) 40%, transparent);
}
.apv-dup-lock .mdi { color: var(--c-error); font-size: 16px; }
/* ปุ่มที่ถูกล็อก — ไม่ได้ใช้ disabled จริง (v-hint ต้องยิง event ได้) จึงต้องทำให้ "ดูกดไม่ได้" เอง */
.id-btn[aria-disabled='true'] { opacity: .5; cursor: not-allowed; }
.apv-dup-name { font-weight: 600; color: var(--c-text-heading); font-size: 13px; }
.apv-dup-meta { font-size: 12.5px; color: var(--c-text-muted); }
/* ตารางเทียบคู่ซ้ำ — คอลัมน์ป้าย | ใบที่ส่งมา | เด็กที่มีอยู่แล้ว · ช่องที่ตรงกันเน้นสี */
.apv-cmp { margin-top: 8px; padding: 10px 12px; border-radius: 10px; background: var(--c-surface); border: 1px solid var(--c-border); }
.apv-cmp-why { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: 13px; color: var(--c-text-body); margin-bottom: 8px; }
.apv-cmp-grid { display: grid; grid-template-columns: max-content 1fr 1fr; gap: 4px 14px; font-size: 13px; color: var(--c-text-body); }
.apv-cmp-col { font-weight: 700; color: var(--c-text-heading); padding-bottom: 2px; border-bottom: 1px solid var(--c-border); }
.apv-cmp-label { color: var(--c-text-muted); }
.apv-cmp-grid .same { font-weight: 700; color: var(--c-text-heading); background: color-mix(in srgb, var(--c-warning) 18%, transparent); border-radius: 4px; padding: 0 4px; margin: 0 -4px; }
.apv-cmp-foot { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; margin-top: 10px; padding-top: 8px; border-top: 1px dashed var(--c-border); }
.apv-cmp-foot .apv-dup-hint { margin: 0; flex: 1 1 260px; }
.dup-conf { display: inline-block; padding: 1px 8px; border-radius: 999px; font-size: 11.5px; font-weight: 700; color: #fff; background: var(--c-warning); }
.dup-conf.cf-veryhigh { background: var(--c-error); }
.dup-conf.cf-medium { background: var(--c-text-muted); }
/* dialog ผูก */
.lk-what { margin: 14px 0 4px; font-weight: 700; color: var(--c-text-heading); }
.lk-steps { margin: 0; padding-left: 20px; line-height: 1.7; }
.apv-tag { display: inline-block; white-space: nowrap; margin-left: 5px; padding: 0 6px; border-radius: 999px; font-size: 10.5px; font-weight: 600; line-height: 1.55; vertical-align: 1px;
  color: var(--c-text-heading); background: color-mix(in srgb, var(--c-warning) 25%, transparent); border: 1px solid color-mix(in srgb, var(--c-warning) 50%, transparent); }
.lk-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.lk-table th, .lk-table td { text-align: left; padding: 6px 8px; border-bottom: 1px solid var(--c-border); vertical-align: top; }
.lk-table th { font-weight: 700; color: var(--c-text-heading); }
.lk-table td:first-child { color: var(--c-text-muted); white-space: nowrap; }
.lk-table tr.changed td:last-child { font-weight: 700; color: var(--c-text-heading); }
.lk-chg { margin-left: 6px; padding: 0 6px; border-radius: 999px; font-size: 11px; font-weight: 700; color: #fff; background: var(--c-primary); }
@media (max-width: 600px) { .apv-dup { margin-left: 0; } }
</style>
